import { httpClient } from "@/shared/http/httpClient";
import { OperationError } from "@/shared/errors/auth-errors";
import { dailyDates } from "@/features/daily/components/daily-dates";

// Wire contract, COPIED (not imported) from the backend's session-diagnostics DTOs
// (`backend/src/modules/session-diagnostics/domain/{session.types,funnel,outcome-stage}.ts`
// and `list-attempts.use-case.ts`): those files resolve `@/` against the backend, so the
// frontend's tsc cannot follow them. Every response is shape-checked below before use.
export type SemanticEventType =
    | "step_viewed" | "quote_requested" | "quote_succeeded" | "quote_failed" | "submit_started"
    | "order_created" | "order_failed" | "order_uncertain" | "capture_gap" | "capture_stopped";
export type Stage = "entry" | "amount" | "quote" | "recipient" | "review" | "confirm" | "payment" | "status" | "unknown";
export type DiagnosticCode =
    | "none" | "validation" | "kyc_required" | "address_required" | "limit_exceeded" | "quote_expired"
    | "network" | "timeout" | "server_error" | "unauthorized" | "unknown";
export type SessionState = "open" | "closed" | "purging" | "expired" | "deleted";
export type Coverage = "live" | "delayed" | "disconnected" | "not_captured" | "ended" | "expired" | "forbidden";

export type SessionCampaign = { source: string | null; medium: string | null; campaign: string | null };

export type SessionSummaryDto = {
    segmentId: string;
    chainId: string;
    app: string;
    identity: "account" | "anonymous";
    campaign: SessionCampaign | null;
    state: SessionState;
    segmentOriginAt: number;
    lastEvidenceAt: number | null;
    lastContiguousSeq: number;
    replayAvailable: boolean;
    coverage: Coverage;
};

export type SessionEventDto = {
    eventId: string;
    attemptId: string;
    submissionId: string | null;
    type: SemanticEventType;
    stage: Stage;
    code: DiagnosticCode;
    quoteIdClaim: string | null;
    orderIdClaim: string | null;
    occurredAt: number;
};

export type ReplayChunkDto = {
    seq: number;
    kind: "full" | "incremental";
    occurredAtFirst: number;
    occurredAtLast: number;
    payloadGzipB64: string;
};

export type SessionListResponse = { sessions: SessionSummaryDto[]; nextCursor: string | null };

// vb-metrics#38 U7 — the enriched session (backend `session.types.ts`, SessionDetailResponse).
export type ConsoleLevel = "info" | "warn" | "error";
export type JsErrorSource = "runtime" | "rejection" | "render" | "chunk";
export type JsErrorStatus = "new" | "recurring";

export type SessionDeviceDto = {
    model: string | null;
    os: string | null;
    osVersion: string | null;
    browser: string | null;
    browserVersion: string | null;
    viewportW: number | null;
    viewportH: number | null;
    language: string | null;
    timeZone: string | null;
};

export type SessionLocationDto = { city: string | null; country: string | null };

export type SessionIdentityDto = { accountId: string; displayName: string | null };

export type SessionNetworkDto = {
    id: string;
    occurredAt: number;
    method: string;
    pathTemplate: string;
    // 0 = no response (network failure, timeout).
    status: number;
    durationMs: number;
    retryOf: string | null;
    requestBody: string | null;
    responseBody: string | null;
    errorRef: string | null;
};

export type SessionConsoleDto = { id: string; occurredAt: number; level: ConsoleLevel; message: string };

export type SessionErrorImpactDto = { sessions: number; users: number; devices: number; firstSeenAt: number };

export type SessionJsErrorDto = {
    id: string;
    occurredAt: number;
    source: JsErrorSource;
    message: string | null;
    stack: string | null;
    errorClass: string | null;
    errorCode: string | null;
    route: string | null;
    fingerprint: string | null;
    impact7d: SessionErrorImpactDto | null;
    status: JsErrorStatus | null;
    networkRef: string | null;
};

export type SessionEnrichmentDto = {
    device: SessionDeviceDto | null;
    location: SessionLocationDto | null;
    release: string | null;
    identity: SessionIdentityDto | null;
    network: SessionNetworkDto[];
    console: SessionConsoleDto[];
    jsErrors: SessionJsErrorDto[];
    telemetryTruncated: boolean;
    telemetrySkipped: number;
    nextSegmentId: string | null;
};

export type SessionDetailResponse = {
    session: SessionSummaryDto;
    events: SessionEventDto[];
    eventsTruncated: boolean;
    eventsSkipped: number;
};

// A backend from before U7 sends no enrichment. `getSession` validates it when present and fills
// every absent block in; `sessionDetailEnrichment.of` reads it typed.
export type SessionEnrichedDetailResponse = SessionDetailResponse & SessionEnrichmentDto;

export type SessionProgressResponse = { session: SessionSummaryDto; reachedStatusAt: number | null };

export type PollSegmentResponse = {
    segmentId: string;
    chunks: ReplayChunkDto[];
    lastContiguousSeq: number;
    gapOpen: boolean;
    needFullSnapshot: boolean;
    coverage: Coverage;
};

export type PollResponse = { segments: PollSegmentResponse[] };

export type CohortWindow = { from: number; to: number };
export type Ratio = { numerator: number; denominator: number };
export type CampaignKey = { source: string; medium: string; campaign: string };
export type ReconciliationStatus = "verified" | "unreconciled" | "conflict" | "declared" | "not_claimed";
export type OperationalStage =
    | "payment_pending" | "funds_received" | "compliance_hold" | "in_process" | "final" | "closed_unsuccessful" | "unknown";

export type FunnelSummary = {
    attempts: number;
    steps: { stage: Stage; attempts: Ratio }[];
    submitted: Ratio;
    orderClaimed: Ratio;
    verified: Ratio;
    final: Ratio;
    reconciliation: Record<ReconciliationStatus, number>;
    verifiedAmounts: { currency: string; amount: number; basis: "order_origin_amount" }[];
};

export type CampaignFunnel = FunnelSummary & { campaign: CampaignKey; campaignBasis: "first_touch_declared" };

export type CohortMeta = { attemptsObserved: number; anonymousExcluded: number; eventsSkipped: number };

export type FunnelReport = {
    window: CohortWindow;
    timeZone: string;
    cohortBasis: "attempt_first_event";
    campaignBasis: "first_touch_declared";
    population: CohortMeta & { attempts: number };
    overall: FunnelSummary;
    campaigns: CampaignFunnel[];
};

export type AttendableAttempt = {
    attemptId: string;
    startedAt: number;
    lastEventAt: number;
    ageMs: number;
    lastStage: Stage;
    lastType: SemanticEventType;
    blockedBy: DiagnosticCode | null;
    blockHistory: DiagnosticCode[];
    reconciliation: ReconciliationStatus;
    operationalStage: OperationalStage | null;
    identity: "account" | "anonymous";
    campaign: CampaignKey;
    segmentIds: string[];
    inactive: boolean | null;
};

export type AttemptListResponse = {
    window: CohortWindow;
    population: CohortMeta & { attempts: number };
    attendable: number;
    attempts: AttendableAttempt[];
    truncated: boolean;
};

export type Signal = {
    ruleId: string;
    code: DiagnosticCode;
    volume: number;
    rate: Ratio;
    period: CohortWindow;
    coverage: CohortMeta & { attempts: number };
};

export type SignalsReport =
    | { status: "configuration_incomplete"; rulesVersion: null; signals: [] }
    | { status: "configured"; rulesVersion: number; signals: Signal[] };

export const SESSIONS_BASE = "/api/v1/diagnostic/sessions";
export const MAX_POLL_SEGMENTS = 12;
// Copied from the backend cursor (`replay-cursor.ts` MAX_CHUNKS_PER_SEGMENT): a poll never
// delivers more chunks per segment, so a FULL page means more recorded history is waiting.
export const POLL_CHUNKS_PER_SEGMENT = 8;
export const JOIN_AT_LATEST_FULL = -1;
// A gzip bomb inside a 64 KiB batch must not exhaust the viewer's memory.
export const MAX_DECODED_PAYLOAD_BYTES = 8 * 1024 * 1024;

export class SessionContractError extends OperationError {}

// Records keyed by the copied vocabularies: tsc rejects them if a type above gains or
// loses a value without its runtime list following.
const COVERAGE_KEYS: Record<Coverage, true> = {
    live: true,
    delayed: true,
    disconnected: true,
    not_captured: true,
    ended: true,
    expired: true,
    forbidden: true,
};
const STAGE_KEYS: Record<Stage, true> = {
    entry: true,
    amount: true,
    quote: true,
    recipient: true,
    review: true,
    confirm: true,
    payment: true,
    status: true,
    unknown: true,
};
const CODE_KEYS: Record<DiagnosticCode, true> = {
    none: true,
    validation: true,
    kyc_required: true,
    address_required: true,
    limit_exceeded: true,
    quote_expired: true,
    network: true,
    timeout: true,
    server_error: true,
    unauthorized: true,
    unknown: true,
};
const EVENT_TYPE_KEYS: Record<SemanticEventType, true> = {
    step_viewed: true,
    quote_requested: true,
    quote_succeeded: true,
    quote_failed: true,
    submit_started: true,
    order_created: true,
    order_failed: true,
    order_uncertain: true,
    capture_gap: true,
    capture_stopped: true,
};

const hasKey = <K extends string>(keys: Record<K, true>, value: unknown): value is K =>
    typeof value === "string" && Object.prototype.hasOwnProperty.call(keys, value);

export const sessionVocabulary = {
    stages: Object.keys(STAGE_KEYS).filter((value): value is Stage => hasKey(STAGE_KEYS, value)),
    codes: Object.keys(CODE_KEYS).filter((value): value is DiagnosticCode => hasKey(CODE_KEYS, value)),
    isCoverage: (value: unknown): value is Coverage => hasKey(COVERAGE_KEYS, value),
    isStage: (value: unknown): value is Stage => hasKey(STAGE_KEYS, value),
    isCode: (value: unknown): value is DiagnosticCode => hasKey(CODE_KEYS, value),
    isEventType: (value: unknown): value is SemanticEventType => hasKey(EVENT_TYPE_KEYS, value),
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const isInteger = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value);

const isSummary = (value: unknown): boolean =>
    isRecord(value)
    && typeof value.segmentId === "string"
    && typeof value.app === "string"
    && (value.identity === "account" || value.identity === "anonymous")
    && (value.campaign === null || isRecord(value.campaign))
    && isInteger(value.segmentOriginAt)
    && (value.lastEvidenceAt === null || isInteger(value.lastEvidenceAt))
    && typeof value.replayAvailable === "boolean"
    && sessionVocabulary.isCoverage(value.coverage);

const isEvent = (value: unknown): boolean =>
    isRecord(value)
    && typeof value.eventId === "string"
    && typeof value.attemptId === "string"
    && sessionVocabulary.isEventType(value.type)
    && sessionVocabulary.isStage(value.stage)
    && sessionVocabulary.isCode(value.code)
    && (value.quoteIdClaim === null || typeof value.quoteIdClaim === "string")
    && (value.orderIdClaim === null || typeof value.orderIdClaim === "string")
    && isInteger(value.occurredAt);

const isChunk = (value: unknown): value is ReplayChunkDto =>
    isRecord(value)
    && isInteger(value.seq)
    && (value.kind === "full" || value.kind === "incremental")
    && isInteger(value.occurredAtFirst)
    && isInteger(value.occurredAtLast)
    && typeof value.payloadGzipB64 === "string";

const isListResponse = (value: unknown): value is SessionListResponse =>
    isRecord(value)
    && Array.isArray(value.sessions)
    && value.sessions.every(isSummary)
    && (value.nextCursor === null || typeof value.nextCursor === "string");

const isProgressResponse = (value: unknown): value is SessionProgressResponse =>
    isRecord(value)
    && isSummary(value.session)
    && (value.reachedStatusAt === null || isInteger(value.reachedStatusAt));

const isNullableString = (value: unknown): value is string | null => value === null || typeof value === "string";
const isNullableInteger = (value: unknown): value is number | null => value === null || isInteger(value);
const isOneOf = <K extends string>(values: readonly K[], value: unknown): value is K =>
    typeof value === "string" && values.some((candidate) => candidate === value);

const CONSOLE_LEVELS: readonly ConsoleLevel[] = ["info", "warn", "error"];
const JS_ERROR_SOURCES: readonly JsErrorSource[] = ["runtime", "rejection", "render", "chunk"];
const JS_ERROR_STATUSES: readonly JsErrorStatus[] = ["new", "recurring"];

const isDevice = (value: unknown): value is SessionDeviceDto =>
    isRecord(value)
    && isNullableString(value.model)
    && isNullableString(value.os)
    && isNullableString(value.osVersion)
    && isNullableString(value.browser)
    && isNullableString(value.browserVersion)
    && isNullableInteger(value.viewportW)
    && isNullableInteger(value.viewportH)
    && isNullableString(value.language)
    && isNullableString(value.timeZone);

const isLocation = (value: unknown): value is SessionLocationDto =>
    isRecord(value) && isNullableString(value.city) && isNullableString(value.country);

const isIdentity = (value: unknown): value is SessionIdentityDto =>
    isRecord(value) && typeof value.accountId === "string" && isNullableString(value.displayName);

const isNetwork = (value: unknown): value is SessionNetworkDto =>
    isRecord(value)
    && typeof value.id === "string"
    && isInteger(value.occurredAt)
    && typeof value.method === "string"
    && typeof value.pathTemplate === "string"
    && isInteger(value.status)
    && typeof value.durationMs === "number"
    && isNullableString(value.retryOf)
    && isNullableString(value.requestBody)
    && isNullableString(value.responseBody)
    && isNullableString(value.errorRef);

const isConsole = (value: unknown): value is SessionConsoleDto =>
    isRecord(value)
    && typeof value.id === "string"
    && isInteger(value.occurredAt)
    && isOneOf(CONSOLE_LEVELS, value.level)
    && typeof value.message === "string";

const isImpact = (value: unknown): value is SessionErrorImpactDto =>
    isRecord(value)
    && isInteger(value.sessions)
    && isInteger(value.users)
    && isInteger(value.devices)
    && isInteger(value.firstSeenAt);

const isJsError = (value: unknown): value is SessionJsErrorDto =>
    isRecord(value)
    && typeof value.id === "string"
    && isInteger(value.occurredAt)
    && isOneOf(JS_ERROR_SOURCES, value.source)
    && isNullableString(value.message)
    && isNullableString(value.stack)
    && isNullableString(value.errorClass)
    && isNullableString(value.errorCode)
    && isNullableString(value.route)
    && isNullableString(value.fingerprint)
    && (value.impact7d === null || isImpact(value.impact7d))
    && (value.status === null || isOneOf(JS_ERROR_STATUSES, value.status))
    && isNullableString(value.networkRef);

// Absent = a backend from before U7 (the view leaves the block out); present = must be well-formed.
const absentOr = <T>(value: unknown, guard: (candidate: unknown) => candidate is T): boolean =>
    value === undefined || guard(value);
const isNullOr = <T>(guard: (candidate: unknown) => candidate is T) =>
    (value: unknown): value is T | null => value === null || guard(value);
const isListOf = <T>(guard: (candidate: unknown) => candidate is T) =>
    (value: unknown): value is T[] => Array.isArray(value) && value.every(guard);
const isBoolean = (value: unknown): value is boolean => typeof value === "boolean";

const isDetailResponse = (value: unknown): value is SessionDetailResponse =>
    isRecord(value)
    && isSummary(value.session)
    && Array.isArray(value.events)
    && value.events.every(isEvent)
    && typeof value.eventsTruncated === "boolean"
    && isInteger(value.eventsSkipped)
    && absentOr(value.device, isNullOr(isDevice))
    && absentOr(value.location, isNullOr(isLocation))
    && absentOr(value.release, isNullableString)
    && absentOr(value.identity, isNullOr(isIdentity))
    && absentOr(value.network, isListOf(isNetwork))
    && absentOr(value.console, isListOf(isConsole))
    && absentOr(value.jsErrors, isListOf(isJsError))
    && absentOr(value.telemetryTruncated, isBoolean)
    && absentOr(value.telemetrySkipped, isInteger)
    && absentOr(value.nextSegmentId, isNullableString);

const readOr = <T>(value: unknown, guard: (candidate: unknown) => candidate is T, fallback: T): T =>
    guard(value) ? value : fallback;

const enrichmentOf = (detail: SessionDetailResponse): SessionEnrichmentDto => {
    const raw: Record<string, unknown> = { ...detail };
    return {
        device: readOr(raw.device, isNullOr(isDevice), null),
        location: readOr(raw.location, isNullOr(isLocation), null),
        release: readOr(raw.release, isNullableString, null),
        identity: readOr(raw.identity, isNullOr(isIdentity), null),
        network: readOr(raw.network, isListOf(isNetwork), []),
        console: readOr(raw.console, isListOf(isConsole), []),
        jsErrors: readOr(raw.jsErrors, isListOf(isJsError), []),
        telemetryTruncated: readOr(raw.telemetryTruncated, isBoolean, false),
        telemetrySkipped: readOr(raw.telemetrySkipped, isInteger, 0),
        nextSegmentId: readOr(raw.nextSegmentId, isNullableString, null),
    };
};

const detailOf = (detail: SessionDetailResponse): SessionEnrichedDetailResponse => ({
    ...detail,
    ...enrichmentOf(detail),
});

const isPollSegment = (value: unknown): value is PollSegmentResponse =>
    isRecord(value)
    && typeof value.segmentId === "string"
    && Array.isArray(value.chunks)
    && value.chunks.every(isChunk)
    && isInteger(value.lastContiguousSeq)
    && typeof value.gapOpen === "boolean"
    && typeof value.needFullSnapshot === "boolean"
    && sessionVocabulary.isCoverage(value.coverage);

const isPollResponse = (value: unknown): value is PollResponse =>
    isRecord(value) && Array.isArray(value.segments) && value.segments.every(isPollSegment);

const isRatio = (value: unknown): boolean =>
    isRecord(value) && isInteger(value.numerator) && isInteger(value.denominator);

const isFunnelSummary = (value: unknown): boolean =>
    isRecord(value)
    && isInteger(value.attempts)
    && Array.isArray(value.steps)
    && isRatio(value.submitted)
    && isRatio(value.orderClaimed)
    && isRatio(value.verified)
    && isRatio(value.final)
    && isRecord(value.reconciliation)
    && Array.isArray(value.verifiedAmounts);

const isWindow = (value: unknown): boolean => isRecord(value) && isInteger(value.from) && isInteger(value.to);

const isPopulation = (value: unknown): boolean =>
    isRecord(value)
    && isInteger(value.attempts)
    && isInteger(value.attemptsObserved)
    && isInteger(value.anonymousExcluded)
    && isInteger(value.eventsSkipped);

const isFunnelReport = (value: unknown): value is FunnelReport =>
    isRecord(value)
    && isWindow(value.window)
    && value.campaignBasis === "first_touch_declared"
    && isPopulation(value.population)
    && isFunnelSummary(value.overall)
    && Array.isArray(value.campaigns)
    && value.campaigns.every((campaign) => isFunnelSummary(campaign) && isRecord(campaign) && isRecord(campaign.campaign));

const isAttemptList = (value: unknown): value is AttemptListResponse =>
    isRecord(value)
    && isWindow(value.window)
    && isPopulation(value.population)
    && isInteger(value.attendable)
    && Array.isArray(value.attempts)
    && value.attempts.every((attempt) =>
        isRecord(attempt)
        && typeof attempt.attemptId === "string"
        && sessionVocabulary.isStage(attempt.lastStage)
        && Array.isArray(attempt.blockHistory)
        && Array.isArray(attempt.segmentIds))
    && typeof value.truncated === "boolean";

const isSignalsReport = (value: unknown): value is SignalsReport =>
    isRecord(value)
    && ((value.status === "configuration_incomplete" && Array.isArray(value.signals) && value.signals.length === 0)
        || (value.status === "configured" && isInteger(value.rulesVersion) && Array.isArray(value.signals)
            && value.signals.every((signal) => isRecord(signal) && isRatio(signal.rate) && isWindow(signal.period))));

const checked = <T>(value: unknown, guard: (candidate: unknown) => candidate is T, what: string): T => {
    if (guard(value)) return value;
    throw new SessionContractError(`session_diagnostics_contract:${what}`);
};

// Only ids, codes and dates ever travel in a URL (FR-011/§7): the workspace is an id.
const query = (params: Record<string, string>): string => new URLSearchParams(params).toString();

const listSessions = async (workspaceId: string, cursor: string | null, signal?: AbortSignal): Promise<SessionListResponse> => {
    const params: Record<string, string> = cursor === null ? { workspaceId } : { workspaceId, cursor };
    return checked(await httpClient.getJson<unknown>(`${SESSIONS_BASE}?${query(params)}`, undefined, signal), isListResponse, "list");
};

const getSession = async (workspaceId: string, segmentId: string, signal?: AbortSignal): Promise<SessionDetailResponse> =>
    detailOf(
        checked(
            await httpClient.getJson<unknown>(`${SESSIONS_BASE}/${encodeURIComponent(segmentId)}?${query({ workspaceId })}`, undefined, signal),
            isDetailResponse,
            "detail",
        ),
    );

// The backend's opaque not-found (FR-011): the same answer for a missing, foreign or expired segment.
export const SESSION_NOT_FOUND = "session_not_found";

// `null` = the segment is not (or no longer) there; any other failure is thrown.
const progress = async (workspaceId: string, segmentId: string, since: number, signal?: AbortSignal): Promise<SessionProgressResponse | null> => {
    try {
        return checked(
            await httpClient.getJson<unknown>(
                `${SESSIONS_BASE}/${encodeURIComponent(segmentId)}/progress?${query({ workspaceId, since: String(since) })}`,
                undefined,
                signal,
            ),
            isProgressResponse,
            "progress",
        );
    } catch (error) {
        if (error instanceof OperationError && error.message === SESSION_NOT_FOUND) return null;
        throw error;
    }
};

const poll = async (
    workspaceId: string,
    segments: { segmentId: string; afterSeq: number }[],
    signal?: AbortSignal,
): Promise<PollResponse> =>
    checked(await httpClient.postJson<unknown>(`${SESSIONS_BASE}/poll`, { workspaceId, segments }, undefined, signal), isPollResponse, "poll");

export type AnalyticsWindowQuery = { date: string } | { from: string; to: string };

const analyticsParams = (workspaceId: string, window: AnalyticsWindowQuery): string =>
    query("date" in window ? { workspaceId, date: window.date } : { workspaceId, from: window.from, to: window.to });

const getFunnel = async (workspaceId: string, window: AnalyticsWindowQuery, signal?: AbortSignal): Promise<FunnelReport> =>
    checked(await httpClient.getJson<unknown>(`${SESSIONS_BASE}/funnel?${analyticsParams(workspaceId, window)}`, undefined, signal), isFunnelReport, "funnel");

const getAttempts = async (workspaceId: string, window: AnalyticsWindowQuery, signal?: AbortSignal): Promise<AttemptListResponse> =>
    checked(await httpClient.getJson<unknown>(`${SESSIONS_BASE}/attempts?${analyticsParams(workspaceId, window)}`, undefined, signal), isAttemptList, "attempts");

const getSignals = async (workspaceId: string, window: AnalyticsWindowQuery, signal?: AbortSignal): Promise<SignalsReport> =>
    checked(await httpClient.getJson<unknown>(`${SESSIONS_BASE}/signals?${analyticsParams(workspaceId, window)}`, undefined, signal), isSignalsReport, "signals");

export type PayloadCodecDeps = {
    decompression: typeof DecompressionStream | undefined;
    textDecoder: typeof TextDecoder | undefined;
};

const browserCodecDeps = (): PayloadCodecDeps => ({
    decompression: typeof DecompressionStream === "undefined" ? undefined : DecompressionStream,
    textDecoder: typeof TextDecoder === "undefined" ? undefined : TextDecoder,
});

const base64Bytes = (b64: string): Uint8Array<ArrayBuffer> | null => {
    try {
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
        return bytes;
    } catch {
        return null;
    }
};

// The browser's own gunzip (no extra dependency). `null` = this chunk cannot be read,
// which the cursor treats as a hole — never as an empty scene.
const decodePayload = async (b64: string, deps: PayloadCodecDeps = browserCodecDeps()): Promise<unknown | null> => {
    if (deps.decompression === undefined || deps.textDecoder === undefined) return null;
    const bytes = base64Bytes(b64);
    if (bytes === null) return null;
    try {
        const stream = new deps.decompression("gzip");
        const writer = stream.writable.getWriter();
        writer.write(bytes).catch(() => undefined);
        writer.close().catch(() => undefined);
        const reader = stream.readable.getReader();
        const parts: Uint8Array[] = [];
        let total = 0;
        for (;;) {
            const { done, value } = await reader.read();
            if (done === true) break;
            total += value.length;
            if (total > MAX_DECODED_PAYLOAD_BYTES) {
                await reader.cancel();
                return null;
            }
            parts.push(value);
        }
        const joined = new Uint8Array(total);
        let offset = 0;
        for (const part of parts) {
            joined.set(part, offset);
            offset += part.length;
        }
        const parsed: unknown = JSON.parse(new deps.textDecoder().decode(joined));
        return parsed;
    } catch {
        return null;
    }
};

// A chunk's payload is an event LIST; anything else is unreadable.
const decodeEvents = async (b64: string, deps: PayloadCodecDeps = browserCodecDeps()): Promise<unknown[] | null> => {
    const decoded = await decodePayload(b64, deps);
    return Array.isArray(decoded) ? decoded : null;
};

export const sessionDetailEnrichment = { of: enrichmentOf };

export const sessionDiagnosticsApi = {
    listSessions,
    getSession,
    progress,
    poll,
    getFunnel,
    getAttempts,
    getSignals,
    decodePayload,
    decodeEvents,
};

export type SessionFilters = {
    from: string | null;
    to: string | null;
    campaign: string | null;
    stage: Stage | null;
    code: DiagnosticCode | null;
    attemptId: string | null;
    segmentId: string | null;
    account: string | null;
    workspaceId: string | null;
};

export const FILTER_KEYS = ["from", "to", "campaign", "stage", "code", "attemptId", "segmentId", "account", "workspaceId"] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EPOCH_MS = /^\d{1,15}$/;
const CAMPAIGN_TOKEN = /^[a-z0-9_-]{1,64}$/;
// The account filter is the opaque HMAC reference (hex SHA-256) — an email can never match.
const ACCOUNT_REF = /^[a-f0-9]{64}$/;
// DA-26: the canonical workspace id (not PII) the detail needs for its read.
export const WORKSPACE_ID = /^[A-Za-z0-9_-]{1,64}$/;

const isInstant = (value: string): boolean => EPOCH_MS.test(value) || dailyDates.isIsoDate(value);

// DA-23 + DA-26: the only query the viewer's URL may carry. Every value is re-validated on the
// way OUT as well, so no code path can put a free-text value (an email) into the URL.
const FILTER_RULES: Record<FilterKey, (value: string) => boolean> = {
    from: isInstant,
    to: isInstant,
    campaign: (value) => CAMPAIGN_TOKEN.test(value) || value === "redacted",
    stage: (value) => sessionVocabulary.isStage(value),
    code: (value) => sessionVocabulary.isCode(value),
    attemptId: (value) => UUID.test(value),
    segmentId: (value) => UUID.test(value),
    account: (value) => ACCOUNT_REF.test(value),
    workspaceId: (value) => WORKSPACE_ID.test(value),
};

const EMPTY_FILTERS: SessionFilters = {
    from: null,
    to: null,
    campaign: null,
    stage: null,
    code: null,
    attemptId: null,
    segmentId: null,
    account: null,
    workspaceId: null,
};

const parseFilters = (params: URLSearchParams): { filters: SessionFilters; rejected: FilterKey[] } => {
    const rejected: FilterKey[] = [];
    const read = (key: FilterKey): string | null => {
        const value = params.get(key);
        if (value === null || value === "") return null;
        if (FILTER_RULES[key](value) === false) {
            rejected.push(key);
            return null;
        }
        return value;
    };
    const stage = read("stage");
    const code = read("code");
    return {
        filters: {
            from: read("from"),
            to: read("to"),
            campaign: read("campaign"),
            stage: sessionVocabulary.isStage(stage) ? stage : null,
            code: sessionVocabulary.isCode(code) ? code : null,
            attemptId: read("attemptId"),
            segmentId: read("segmentId"),
            account: read("account"),
            workspaceId: read("workspaceId"),
        },
        rejected,
    };
};

const filtersToSearch = (filters: SessionFilters): string => {
    const params = new URLSearchParams();
    for (const key of FILTER_KEYS) {
        const value = filters[key];
        if (value !== null && FILTER_RULES[key](value)) params.set(key, value);
    }
    return params.toString();
};

const DAY_MS = 86_400_000;

const instantOf = (value: string): { kind: "date"; date: string } | { kind: "epoch"; at: number } =>
    EPOCH_MS.test(value) ? { kind: "epoch", at: Number(value) } : { kind: "date", date: value };

// R8: a Madrid calendar day is sent as `date`; a range of days is sent as the epoch-ms
// window from 00:00 Madrid of the first day to 00:00 Madrid of the day after the last.
const windowOf = (filters: SessionFilters, now: number): AnalyticsWindowQuery | null => {
    if (filters.from === null && filters.to === null) return { date: dailyDates.madridDateOf(now) };
    if (filters.from === null) return null;
    const from = instantOf(filters.from);
    const to = filters.to === null ? null : instantOf(filters.to);
    if (from.kind === "date" && (to === null || (to.kind === "date" && to.date === from.date))) return { date: from.date };
    if (from.kind === "date" && to !== null && to.kind === "date" && to.date > from.date) {
        return {
            from: String(dailyDates.madridStartOf(from.date)),
            to: String(dailyDates.madridStartOf(dailyDates.shift(to.date, 1))),
        };
    }
    if (from.kind === "epoch" && to !== null && to.kind === "epoch" && to.at > from.at && to.at - from.at <= 31 * DAY_MS) {
        return { from: String(from.at), to: String(to.at) };
    }
    return null;
};

export const sessionFilters = {
    empty: EMPTY_FILTERS,
    parse: parseFilters,
    toSearch: filtersToSearch,
    windowOf,
};
